import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";

// Default to the month just gone — the one you are about to file for.
function lastMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return [iso(start), iso(end)];
}

export default function TaxReport() {
  const [defFrom, defTo] = useMemo(lastMonthRange, []);
  const [dateFrom, setDateFrom] = useState(defFrom);
  const [dateTo, setDateTo] = useState(defTo);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    setError("");
    api
      .get("/reports/gst", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load the report"));
  }, [dateFrom, dateTo]);

  const net = data?.totals?.net_gst || 0;

  const csv = () => {
    const rows = [
      ["Type", "Ref", "Date", "Party", "GST %", "Net", "GST", "Total"],
      ...(data?.orders || []).map((o) => [
        "Sale", o.invoice_no, o.date.slice(0, 10), o.customer, o.gst_percent,
        o.taxable.toFixed(2), o.gst.toFixed(2), o.total.toFixed(2),
      ]),
      ...(data?.purchases || []).map((p) => [
        "Purchase", p.code, p.date.slice(0, 10), (p.suppliers || []).join(" / "), "",
        p.cost.toFixed(2), p.gst.toFixed(2), (p.cost + p.gst).toFixed(2),
      ]),
      [],
      ["Output GST", "", "", "", "", "", (data?.totals?.gst || 0).toFixed(2)],
      ["Input GST", "", "", "", "", "", (data?.totals?.input_gst || 0).toFixed(2)],
      ["Net payable", "", "", "", "", "", (data?.totals?.net_gst || 0).toFixed(2)],
    ];
    const body = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `gst-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="tax-report-page">
      <div>
        <div className="label-mini">Sales Tax</div>
        <h1 className="text-3xl font-bold tracking-tight mt-1">GST Report</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          className="border border-zinc-200 rounded-sm text-xs px-2 py-1.5"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          data-testid="gst-date-from"
        />
        <span className="text-xs text-zinc-400">to</span>
        <input
          type="date"
          className="border border-zinc-200 rounded-sm text-xs px-2 py-1.5"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          data-testid="gst-date-to"
        />
        <button
          onClick={csv}
          disabled={!data?.orders?.length}
          className="border border-zinc-200 rounded-sm text-xs px-3 py-1.5 disabled:opacity-40"
          data-testid="gst-export-csv"
        >
          Export CSV
        </button>
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5">
          <div className="label-mini">Taxable Sales</div>
          <div className="kpi-num text-2xl mt-2">{fmtMoney(data?.totals?.taxable || 0)}</div>
          <div className="text-xs text-zinc-500 mt-1">
            {data?.totals?.orders || 0} invoices with GST
          </div>
        </div>
        <div className="bg-white p-5" data-testid="gst-output-total">
          <div className="label-mini">Output GST</div>
          <div className="kpi-num text-2xl mt-2">{fmtMoney(data?.totals?.gst || 0)}</div>
          <div className="text-xs text-zinc-500 mt-1">Charged to customers</div>
        </div>
        <div className="bg-white p-5" data-testid="gst-input-total">
          <div className="label-mini">Input GST</div>
          <div className="kpi-num text-2xl mt-2">{fmtMoney(data?.totals?.input_gst || 0)}</div>
          <div className="text-xs text-zinc-500 mt-1">
            Charged by {data?.purchases?.length || 0} supplier bills
          </div>
        </div>
        <div className="bg-white p-5" data-testid="gst-net-total">
          <div className="label-mini">{net < 0 ? "Refund / Carry Forward" : "Net GST Payable"}</div>
          <div className="kpi-num text-2xl mt-2">{fmtMoney(Math.abs(net))}</div>
          <div className="text-xs text-zinc-500 mt-1">Output minus input</div>
        </div>
      </div>

      {(data?.months?.length || 0) > 1 && (
        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Month</th>
                <th className="text-right px-4 py-2 font-medium">Invoices</th>
                <th className="text-right px-4 py-2 font-medium">Taxable</th>
                <th className="text-right px-4 py-2 font-medium">Output GST</th>
                <th className="text-right px-4 py-2 font-medium">Input GST</th>
                <th className="text-right px-4 py-2 font-medium">Net</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m) => (
                <tr key={m.period} className="border-t border-zinc-100">
                  <td className="px-4 py-2">{m.period}</td>
                  <td className="px-4 py-2 text-right">{m.orders}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(m.taxable)}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(m.gst)}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(m.input_gst)}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmtMoney(m.net_gst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="label-mini">Output Tax — Sales Invoices</div>
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Invoice</th>
              <th className="text-left px-4 py-2 font-medium">Date</th>
              <th className="text-left px-4 py-2 font-medium">Customer</th>
              <th className="text-right px-4 py-2 font-medium">GST %</th>
              <th className="text-right px-4 py-2 font-medium">Taxable</th>
              <th className="text-right px-4 py-2 font-medium">GST</th>
              <th className="text-right px-4 py-2 font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {(data?.orders || []).map((o) => (
              <tr key={o.id} className="border-t border-zinc-100">
                <td className="px-4 py-2">
                  <Link to={`/orders/${o.id}`} className="underline underline-offset-2">
                    {o.invoice_no || "—"}
                  </Link>
                </td>
                <td className="px-4 py-2">{fmtDate(o.date)}</td>
                <td className="px-4 py-2">{o.customer}</td>
                <td className="px-4 py-2 text-right">{o.gst_percent}%</td>
                <td className="px-4 py-2 text-right">{fmtMoney(o.taxable)}</td>
                <td className="px-4 py-2 text-right font-medium">{fmtMoney(o.gst)}</td>
                <td className="px-4 py-2 text-right">{fmtMoney(o.total)}</td>
              </tr>
            ))}
            {!data?.orders?.length && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-zinc-400">
                  No GST invoices in this period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="label-mini">Input Tax — Supplier Bills</div>
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Order</th>
              <th className="text-left px-4 py-2 font-medium">Date</th>
              <th className="text-left px-4 py-2 font-medium">Supplier</th>
              <th className="text-right px-4 py-2 font-medium">Cost</th>
              <th className="text-right px-4 py-2 font-medium">Input GST</th>
            </tr>
          </thead>
          <tbody>
            {(data?.purchases || []).map((p) => (
              <tr key={p.id} className="border-t border-zinc-100">
                <td className="px-4 py-2">
                  <Link to={`/orders/${p.id}`} className="underline underline-offset-2">
                    {p.code}
                  </Link>
                </td>
                <td className="px-4 py-2">{fmtDate(p.date)}</td>
                <td className="px-4 py-2">{(p.suppliers || []).join(", ")}</td>
                <td className="px-4 py-2 text-right">{fmtMoney(p.cost)}</td>
                <td className="px-4 py-2 text-right font-medium">{fmtMoney(p.gst)}</td>
              </tr>
            ))}
            {!data?.purchases?.length && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-zinc-400">
                  No supplier GST in this period — set a supplier's GST % on their profile
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-zinc-500">
        Billed orders only — quotations and cancelled orders are excluded from both sides.
        Input tax comes from each supplier's GST % on their profile; imports and unregistered
        vendors sit at 0. GST paid at customs on imports is not tracked here.
      </p>
    </div>
  );
}
